import React, { Fragment, useState } from 'react';
import Box from 'components/atoms/Box/Box';
import Typography from 'components/atoms/Typography/Typography';
import Button from 'components/atoms/Button/Button';
import TextInput from 'components/atoms/TextInput/TextInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import CheckboxGroup from 'components/molecules/CheckboxGroup/CheckboxGroup';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  heightInputAtom,
  basicWellnessInfoFormSubmitAtom,
  weightInputAtom,
  fitnessPreferencesCheckboxGroupAtom,
  fitnessPreferencesFormSubmitAtom,
  profileInfoAtom,
  profileInfoFormSubmitAtom,
  nutritionPreferencesCheckboxGroupAtom,
  nutritionPreferencesFormSubmitAtom,
} from 'dataLayer/recoil/pages/myProfile/atoms';
import {
  fitnessPreferencesSelectorPut,
  nutritionPreferencesSelectorPut,
  profileInfoSelectorPut,
  wellnessProfileSelectorPut,
} from 'dataLayer/recoil/pages/myProfile/selectors';
import Divider from 'components/atoms/Divider/Divider';
import Title from 'components/atoms/Title/Title';
import { UpdateStatusLoadable } from 'dataLayer/recoil/UpdateStatusLoadable';
import FormControl from 'components/atoms/FormControl/FormControl';
import { InputLabel, MenuItem, Select } from '@material-ui/core';
import {
  apiDeleteGenotype,
  apiGetGenotype,
  apiPostGenotype,
} from 'dataLayer/api/apiClient';
import Paragraph from 'components/atoms/Paragraph/Paragraph';
import { genotypeStatusAtom } from 'dataLayer/recoil/apiData/atoms';
import {
  recommenderExercisesRequestIdAtom,
  recommenderRecipesRequestIdAtom,
  recommenderMealplanRequestIdAtom,
} from 'dataLayer/recoil/apiData/requestIdAtoms';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguageDirection } from 'helpers/rtlHelper';

export default function MyProfile(props) {
  const { t, i18n } = useTranslation();
  const [genotypeStatus, setGenotypeStatus] = useRecoilState(
    genotypeStatusAtom
  );
  const [profileInfo, setProfileInfo] = useRecoilState(profileInfoAtom);
  const [height, setHeight] = useRecoilState(heightInputAtom);
  const [weight, setWeight] = useRecoilState(weightInputAtom);
  const [fitnessPreferences, setFitnessPreferences] = useRecoilState(
    fitnessPreferencesCheckboxGroupAtom
  );
  const [nutritionPreferences, setNutritionPreferences] = useRecoilState(
    nutritionPreferencesCheckboxGroupAtom
  );
  const [
    recommenderRecipesRequestId,
    setRecommenderRecipesRequestId,
  ] = useRecoilState(recommenderRecipesRequestIdAtom);
  const [
    recommenderMealplanRequestId,
    setRecommenderMealplanRequestId,
  ] = useRecoilState(recommenderMealplanRequestIdAtom);
  const [
    recommenderExercisesRequestId,
    setRecommenderExercisesRequestId,
  ] = useRecoilState(recommenderExercisesRequestIdAtom);

  const setProfileInfoForm = useSetRecoilState(profileInfoFormSubmitAtom);
  const setBasicWellnessInfoForm = useSetRecoilState(
    basicWellnessInfoFormSubmitAtom
  );
  const setFitnessPreferencesForm = useSetRecoilState(
    fitnessPreferencesFormSubmitAtom
  );
  const setNutritionPreferencesForm = useSetRecoilState(
    nutritionPreferencesFormSubmitAtom
  );

  const handleProfileInfoChange = (e) => {
    const { name, value } = e.target;

    let newProfileInfo;
    if (name === 'year_of_birth' || name === 'gender') {
      //only year of birth and gender are saved in their fields on API
      newProfileInfo = { ...profileInfo, [name]: value };
    } else {
      //rest of the fields go to extra_data field
      newProfileInfo = {
        ...profileInfo,
        extra_data: { ...profileInfo.extra_data, [name]: value },
      };
    }

    setProfileInfo(newProfileInfo);
  };

  const [uploadProgress, setUploadProgress] = useState(0);

  const handleProfileInfoSave = () => {
    setProfileInfoForm(profileInfo);

    if (
      profileInfo &&
      profileInfo.extra_data &&
      profileInfo.extra_data.language
    ) {
      i18n.changeLanguage(profileInfo.extra_data.language);
      window.localStorage.setItem('lan_xh', profileInfo.extra_data.language);
      window.localStorage.setItem(
        'lan_dir_xh',
        getCurrentLanguageDirection(profileInfo.extra_data.language)
      );
    }
    document.body.setAttribute(
      'dir',
      getCurrentLanguageDirection(profileInfo.extra_data.language)
    );
    document.documentElement.lang = profileInfo.extra_data.language;
  };

  const handleWellnessProfileSave = () => {
    setBasicWellnessInfoForm({ height, weight });
  };

  const handleFitnessPreferencesSave = () => {
    setRecommenderExercisesRequestId(recommenderExercisesRequestId + 1);
    setFitnessPreferencesForm(fitnessPreferences);
  };

  const handleNutritionPreferencesSave = () => {
    //setRecommenderRecipesRequestId(recommenderRecipesRequestId + 1);
    setRecommenderMealplanRequestId(recommenderMealplanRequestId + 1);
    setNutritionPreferencesForm(nutritionPreferences);
  };

  //TODO: this can be refactored and done through selector to be consistent with other code
  const getUploadUrlAndUploadGenotype = (params, file) => {
    apiPostGenotype(params).then(() => {
      apiGetGenotype().then((resp) => {
        const uploadUrl = resp.source_details.upload_url;

        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);

        xhr.upload.onprogress = (ev) => {
          setUploadProgress(Math.round((ev.loaded * 100) / ev.total));
        };

        xhr.onload = () => {
          const status = xhr.status;
          if (status === 200) {
            setUploadProgress(100);
            setGenotypeStatus(resp);
          }
        };

        xhr.onerror = (err) => {
          console.log(err);
          setUploadProgress(100);
        };
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    });
  };

  //TODO: this can be refactored and done through selector to be consistent with other code
  const handleUploadFileOnChange = async ({ target }) => {
    const file = target.files[0];
    const params = {
      fileupload: {
        content_type: file.type,
        method: 'PUT',
        original_filename: file.name,
      },
    };

    try {
      //genotype needs to be deleted to make upload
      await apiDeleteGenotype();
      getUploadUrlAndUploadGenotype(params, file);
    } catch (error) {
      //API returns 404 when user doesn't have genotype uploaded
      console.log(error.response.data);
      //when there is no genotype data already uploaded or it is deleted previously
      getUploadUrlAndUploadGenotype(params, file);
    }
  };

  const isGenotypeUploaded = genotypeStatus.status === 404 ? false : true;

  return (
    <Fragment>
      <Box>
        <Box mb={2}>
          <Typography variant="h1">
            <Trans i18nKey="dna24.profile.profileInfo" />
          </Typography>
          <Box display="flex" mt={2}>
            <Box mr={2}>
              <TextInput
                label={t('dna24.profile.firstName')}
                name="first_name"
                value={profileInfo.extra_data.first_name}
                onChange={handleProfileInfoChange}
              />
            </Box>

            <Box mr={2}>
              {/* TODO: below code should be our select component and documented in storybook */}
              <FormControl variant="filled">
                <InputLabel>
                  <Trans i18nKey="dna24.profile.gender" />
                </InputLabel>
                <Select
                  name="gender"
                  value={profileInfo.gender}
                  onChange={handleProfileInfoChange}
                >
                  <MenuItem value="male">
                    <Trans i18nKey="dna24.profile.male" />
                  </MenuItem>
                  <MenuItem value="female">
                    <Trans i18nKey="dna24.profile.female" />
                  </MenuItem>
                  <MenuItem value="other">
                    <Trans i18nKey="dna24.profile.other" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box mr={2}>
              {/* TODO: below code should be our select component and documented in storybook */}
              <FormControl variant="filled">
                <InputLabel>
                  <Trans i18nKey="dna24.profile.language" />
                </InputLabel>
                <Select
                  name="language"
                  value={
                    profileInfo.extra_data.language
                      ? profileInfo.extra_data.language
                      : 'en'
                  }
                  onChange={handleProfileInfoChange}
                >
                  <MenuItem value="en">
                    <Trans i18nKey="dna24.profile.english" />
                  </MenuItem>
                  <MenuItem value="ar-AE">
                    <Trans i18nKey="dna24.profile.uae" />
                  </MenuItem>
                  <MenuItem value="he-IL">
                    <Trans i18nKey="dna24.profile.hebrew-israel" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <Box display="flex" my={2}>
            <Box mr={2}>
              <TextInput
                label={t('dna24.profile.yearOfBirth')}
                name="year_of_birth"
                type="number"
                value={profileInfo.year_of_birth}
                onChange={handleProfileInfoChange}
              />
            </Box>

            <Box mr={2}>
              <TextInput
                label={t('dna24.profile.ethnicity')}
                name="ethnicity"
                value={profileInfo.extra_data.ethnicity}
                onChange={handleProfileInfoChange}
              />
            </Box>
          </Box>

          <Button variant="contained" onClick={handleProfileInfoSave}>
            <Trans i18nKey="dna24.profile.save" />
          </Button>
        </Box>
        <UpdateStatusLoadable
          value={profileInfoSelectorPut}
          type="profileInfo"
        />
      </Box>

      <Box mt={3}>
        <Divider />
        <Box mt={2}>
          <Typography variant="h1">
            <Trans i18nKey="dna24.profile.genotypeInfo" />
          </Typography>
          <Box mt={2}>
            <Box mb={2}>
              {isGenotypeUploaded ? (
                <Box>
                  <Paragraph>
                    <Trans i18nKey="dna24.profile.genotypeAlreadyExists" />
                  </Paragraph>
                  <Paragraph>
                    <Trans i18nKey="dna24.profile.genotypeFile" />{' '}
                    {genotypeStatus &&
                      genotypeStatus.source_details &&
                      genotypeStatus.source_details.original_filename}
                  </Paragraph>
                </Box>
              ) : (
                <Paragraph>
                  <Trans i18nKey="dna24.profile.genotypeDoesNotExists" />
                </Paragraph>
              )}
            </Box>
            <Box>
              <Paragraph>
                <Trans i18nKey="dna24.profile.genotypeUploadInstructions" />
              </Paragraph>
              <input type="file" onChange={handleUploadFileOnChange} />
              <Box>
                <Paragraph>
                  {t('dna24.profile.genotypeUploadProgress', {
                    uploadProgress: uploadProgress,
                  })}
                </Paragraph>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box mt={3}>
        <Divider />
        <Box mt={2}>
          <Typography variant="h1">
            <Trans i18nKey="dna24.profile.wellnessInfo" />
          </Typography>
          <Box p={1}>
            <Typography variant="h2">
              <Trans i18nKey="dna24.profile.basicWellnessInfo" />
            </Typography>
            <Box mt={1} display="flex">
              <Box mr={2}>
                <TextInput
                  label={t('dna24.profile.height')}
                  name="heightFirst"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <Box ml={1}>
                        <InputAdornment position="start">
                          <Typography>cm</Typography>
                        </InputAdornment>
                      </Box>
                    ),
                  }}
                ></TextInput>
              </Box>

              {/* <Box mr={1}>
                <TextInput
                  label="height"
                  name="heightSecond"
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <Box ml={1}>
                        <InputAdornment position="start">
                          <Typography>''</Typography>
                        </InputAdornment>
                      </Box>
                    ),
                  }}
                ></TextInput>
              </Box> */}

              <Box mr={2}>
                <TextInput
                  label={t('dna24.profile.weight')}
                  name="weight"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <Box ml={1}>
                        <InputAdornment position="start">
                          <Typography>kg</Typography>
                        </InputAdornment>
                      </Box>
                    ),
                  }}
                ></TextInput>
              </Box>
            </Box>
          </Box>
          <Box m={1}>
            <Button variant="contained" onClick={handleWellnessProfileSave}>
              <Trans i18nKey="dna24.profile.save" />
            </Button>
          </Box>
        </Box>
        <UpdateStatusLoadable value={wellnessProfileSelectorPut} />
      </Box>

      <Box mt={3}>
        <Divider />
        <Box mt={2}>
          <Box p={1}>
            <Title variant="h2">
              <Trans i18nKey="dna24.profile.fitnessPreferences" />
            </Title>
            <Box mt={1} display="flex">
              <CheckboxGroup
                items={fitnessPreferences}
                formLabel={t('dna24.profile.exerciseTypePreference')}
                onSelectionChange={(newData) => setFitnessPreferences(newData)}
              />
            </Box>
          </Box>
          <Box ml={1} mb={1}>
            <Button variant="contained" onClick={handleFitnessPreferencesSave}>
              <Trans i18nKey="dna24.profile.save" />
            </Button>
          </Box>
        </Box>
        <UpdateStatusLoadable value={fitnessPreferencesSelectorPut} />
      </Box>

      <Box mt={3} mb={3}>
        <Divider />

        <Box mt={2}>
          <Box p={1}>
            <Title variant="h2">
              <Trans i18nKey="dna24.profile.nutritionPreferences" />
            </Title>
            <Box mt={1} display="flex">
              <CheckboxGroup
                formLabel={t('dna24.profile.foodTypePreference')}
                items={nutritionPreferences}
                onSelectionChange={(newData) =>
                  setNutritionPreferences(newData)
                }
              />
            </Box>
          </Box>
          <Box ml={1} mb={1}>
            <Button
              variant="contained"
              onClick={handleNutritionPreferencesSave}
            >
              <Trans i18nKey="dna24.profile.save" />
            </Button>
          </Box>
        </Box>
        <UpdateStatusLoadable value={nutritionPreferencesSelectorPut} />
      </Box>
    </Fragment>
  );
}
